<template>
  <div class="main">
    <div id="app">
      <HeaderMain v-if="topbar" />
      <RouterView v-if="isConnected" />
      <Modal />

      <div v-if="showCookies" class="cookies-strip">
        <div class="text">
          {{ $t('CookieStripStart') }}
          <RouterLink :to="{ name: 'Cookies' }" class="more-info">{{ $t('CookieDeclaration') }}</RouterLink>
          {{ $t('CookieStripEnd') }}
        </div>
        <div class="approved" @click="hideCookies">{{ $t('Approve') }}</div>
      </div>
    </div>

    <div class="responsive-view">
      <div class="content">
        <div class="jll-logo"></div>
        <div class="commuter-logo"></div>
        <div class="responsive-message">{{ $t('responsiveMessage') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import config from '@/config';
import { mapGetters } from 'vuex';
import HeaderMain from '@/components/HeaderMain';
import Modal from '@/components/Modal';
import { useSignalR } from '@dreamonkey/vue-signalr';

export default {
  name: 'App',

  components: {
    HeaderMain,
    Modal,
  },

  data() {
    return {
      isConnected: false,
      topbar: false,
    };
  },

  computed: {
    ...mapGetters({
      isLogged: 'isLogged',
      isDesktop: 'isDesktop',
      userInfo: 'userInfo',
      acceptedCookies: 'acceptedCookies',
    }),

    showCookies() {
      const noCookies = ['Report', 'Login', 'Logout', 'OpenId', 'Tv1', 'Tv2', 'Tv3', 'Tv4'];
      return !this.acceptedCookies && !noCookies.includes(this.$route.name) && this.isDesktop;
    },
  },

  watch: {
    $route() {
      this.routeChange();
    },
  },

  created() {
    this.$socket = useSignalR();

    this.$i18n.locale = this.$store.getters.userLanguage;

    this.routeChange();
    if (this.$route.query.controllerId) {
      localStorage.setItem('controllerId', this.$route.query.controllerId);
    }

    if (this.$route.query.socket) {
      localStorage.setItem('socket', this.$route.query.socket);
    }

    if (this.$route.query.controllerId || this.$route.query.socket) {
      this.$router.push({ query: {} });
    }

    if (this.$route.name === 'Report') {
      this.$store.dispatch('enableDesktopMode');
      this.isConnected = true;
      return;
    }

    const allowNoConnect = [
      'OpenId',
      'Tv1',
      'Tv2',
      'Tv3',
      'Tv4',
      'Report',
      'Version',
      'Login',
      'Logout',
      'Unauthorized',
      'user',
      'userLogin',
      'userForgetPassword',
      'userForgetCompleted',
      'userForgetPasswordSecretQuestion',
      'userResetPassword',
      'userRegister',
      'userRegisterSecretQuestion',
      'userCompleted',
      'userResult',
    ];

    if (!this.isLogged && !allowNoConnect.includes(this.$route.name)) {
      const isClientLogin = this.$route.name === 'userResult' || this.$route.query.user;

      this.$router.push({ name: isClientLogin ? 'userLogin' : 'Login' });
    } else if (this.isLogged) {
      this.$store.dispatch('updateUserInfo').catch(() => {
        this.$router.push({ name: 'Logout' });
      });
    }

    const tvs = ['Tv1', 'Tv2', 'Tv3', 'Tv4'];
    const socket = localStorage.getItem('socket');
    if (socket === 'true' && !tvs.includes(this.$route.name)) {
      this.initSocket();
      this.$socket.on('reconect', this.initSocket);
      return;
    }

    this.$store.dispatch('enableDesktopMode');
    this.isConnected = true;
  },

  methods: {
    hideCookies() {
      this.$store.dispatch('acceptCookies');
    },

    async connect(id) {
      this.isConnected = true;
      try {
        console.log('connect');
        await this.$socket.invoke('connect', { id });
      } catch (e) {
        this.isConnected = false;
        if (e.message === 'Room already use.') {
          alert('Room already in use. Switching to desktop mode.');
          console.warn('Room already in use.');
          // this.openPopup('Room already in use. Switching to desktop mode.', 'Warning')
          this.isConnected = true;
          return;
        }

        localStorage.removeItem('controllerId');
        window.location.replace(`${config.NXTROOM_URL}/controller?callbackUri=${window.location}&error=invalid_token`);
      }
    },

    initSocket() {
      const id = localStorage.getItem('controllerId');
      const regex1 = RegExp(/^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i);

      if (!id || !regex1.test(id)) {
        return window.location.replace(`${config.NXTROOM_URL}/controller?callbackUri=${window.location}`);
      }
      return this.connect(id);
    },

    routeChange() {
      this.topbar = this.$route.meta.topbar;
    },
  },

  sockets: {
    connected({ id }) {
      this.isConnected = true;
      this.$socket.invoke('connect', { id });
      localStorage.setItem('controllerId', id);
    },

    unlink() {
      localStorage.removeItem('controllerId');
      this.isConnected = false;
      window.location.replace(`${config.NXTROOM_URL}/controller?callbackUri=${window.location}`);
    },
  },
};
</script>

<style lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/app.scss';

@media only screen {
  .cookies-strip {
    position: fixed;
    bottom: 16px;
    right: calc(50% + 480px);
    left: calc(50% - 480px);
    z-index: 105;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 960px;
    height: 48px;
    padding: 0 24px;
    line-height: 16px;
    border-radius: 48px;
    text-align: center;
    background: rgba(colors.$white-color, 0.95);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

    @media only screen and (max-width: 1024px) {
      right: 16px;
      left: 16px;
      width: auto;
    }

    .text {
      font-size: 14px;

      a {
        text-decoration: underline;
        cursor: pointer;
        transition: 0.2s ease;

        &:hover {
          color: colors.$main-color;
        }
      }
    }

    .approved {
      height: 28px;
      line-height: 28px;
      border-radius: 28px;
      padding: 0 16px;
      margin: auto -8px auto 12px;
      font-size: 14px;
      background: colors.$main-color;
      color: colors.$white-color;
      transition: 0.2s ease;
      cursor: pointer;
      box-shadow: 0 0 6px rgba(0, 0, 0, 0);

      &:hover {
        color: rgba(colors.$white-color, 0.8);
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
      }
    }

    .close {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 48px;
      font-size: 24px;
      transform: rotate(45deg);
      transition: 0.2s ease;
      cursor: pointer;

      &:hover {
        transform: rotate(45deg) scale(1.2);
        color: colors.$main-color;
      }
    }
  }
}

.main:not(.study-button) {
  height: 100%;
}

.responsive-view {
  display: none;
}

@media only screen and (max-width: 780px) {
  #app {
    display: none;
  }

  .responsive-view {
    display: block;
    background: colors.$dark-color;
    color: colors.$white-color;
    align-items: center;
    flex-direction: column;
    width: 100vw;
    height: 100vh;
    flex-shrink: 0;
    overflow: hidden;

    .content {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: 48px 24px;
      flex-shrink: 0;
      box-sizing: border-box;
      overflow-y: auto;

      .jll-logo {
        width: 64px;
        height: 32px;
        flex-shrink: 0;
        background-image: url('./assets/JLLLogo-White.png');
        background-repeat: no-repeat;
        background-position: center center;
        background-size: contain;
      }

      .commuter-logo {
        margin: 32px auto 48px;
        width: 192px;
        height: 96px;
        flex-shrink: 0;
        background-image: url('./assets/CommuterLogo-White.svg');
        background-repeat: no-repeat;
        background-position: center center;
        background-size: contain;
      }

      .responsive-message {
        display: block;
        padding: 48px 24px;
        text-align: center;
      }
    }
  }
}
</style>
